import type { AccessStatusReason, TrialPlan } from './types';

/** Access the learner bought or was granted. A plan left over from their trial never outranks it. */
const PAID_REASONS: readonly AccessStatusReason[] = ['lifetime', 'subscription', 'comp', 'bonus'];

/**
 * Rule 1 + 9: trial dashboard owns /dashboard while the learner is on a trial
 * (or the week is frozen / ended after the trial ends). Paid / comp / lifetime
 * keep today's dashboard. When accessReason is not on the profile payload at all,
 * fall back to an active (or frozen) plan so local fixtures still walk.
 */
export function shouldShowTrialDashboard(opts: {
  flagOn: boolean;
  accessReason?: AccessStatusReason | null;
  plan?: TrialPlan | null;
  /** hasPaidAccess: right after purchase accessStatus can still be null. */
  paid?: boolean;
}): boolean {
  if (!opts.flagOn || !opts.plan || opts.paid) return false;
  if (opts.accessReason && PAID_REASONS.includes(opts.accessReason)) return false;
  // An ended week still owns Home: the ended screen replaces the day view, and
  // only a paid reason (returned above) takes the learner back to the classic page.
  if (opts.plan.frozen || opts.plan.ended) return true;
  if (opts.accessReason === 'trial') return true;
  // Fixture / pre-accessReason rollout: active plan without a reason still shows.
  if (opts.accessReason == null && opts.plan.active) return true;
  return false;
}

/**
 * Rule 1 hides Practice only while the week is still running. Spec section 8:
 * once the week has ended, /practice is the ordinary quiz on the free lane and
 * the hub is Home only, so hiding it here would bounce the learner back to the
 * ended screen every time they opened Practice.
 */
export function shouldHidePracticeTab(opts: {
  flagOn: boolean;
  accessReason?: AccessStatusReason | null;
  plan?: TrialPlan | null;
  paid?: boolean;
}): boolean {
  if (opts.plan?.ended) return false;
  return shouldShowTrialDashboard(opts);
}

